import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Users, TrendingUp, FileText, Headphones, Mail, Zap, FolderOpen, Calendar, BarChart3, PenTool, Shield } from 'lucide-react';

const ZohoOneFunctionaliteiten = () => {
  const functionalities = [
    {
      category: 'Sales en CRM',
      apps: 'Zoho CRM, Zoho Forms, Zoho Sign',
      purpose: 'Leads, klanten en verkoopkansen beheren',
      benefit: 'Meer grip op verkoopproces en betere conversie',
      icon: <Users className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Finance en Administratie',
      apps: 'Zoho Books, Zoho Subscriptions, Zoho Expense',
      purpose: 'Offertes, facturen en betalingen beheren',
      benefit: 'Minder handmatig administratief werk',
      icon: <FileText className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Projecten en Planning',
      apps: 'Zoho Projects, Zoho Sprints',
      purpose: 'Taken, mijlpalen en uren beheren',
      benefit: 'Betere projectcontrole en tijdsbesparing',
      icon: <Calendar className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Klantenservice',
      apps: 'Zoho Desk, Zoho Assist',
      purpose: 'Tickets en klantvragen beheren',
      benefit: 'Snellere klantenservice en hogere tevredenheid',
      icon: <Headphones className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Marketing',
      apps: 'Zoho Campaigns, Zoho Social, Zoho Forms',
      purpose: 'Campagnes en leadgeneratie',
      benefit: 'Meer leads en betere conversie',
      icon: <Mail className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Automatisering',
      apps: 'Zoho Flow, Workflows in apps',
      purpose: 'Apps en processen koppelen',
      benefit: 'Minder dubbele invoer en fouten',
      icon: <Zap className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Documenten',
      apps: 'Zoho WorkDrive, Zoho Sign, Zoho Writer',
      purpose: 'Documenten opslaan en delen',
      benefit: 'Centrale dossiers en veilige samenwerking',
      icon: <FolderOpen className="w-8 h-8 text-blue-700" />,
    },
    {
      category: 'Rapportages',
      apps: 'Zoho Analytics, Dashboards',
      purpose: 'Dashboards en KPI\'s maken',
      benefit: 'Betere managementinformatie en besluitvorming',
      icon: <BarChart3 className="w-8 h-8 text-blue-700" />,
    },
  ];

  const faqs = [
    {
      question: 'Welke functionaliteiten heeft Zoho One?',
      answer: 'Zoho One bevat functionaliteiten voor CRM en verkoop, financiële administratie en facturatie, projectmanagement, klantenservice, e-mailmarketing, HR en personeelsbeheer, documentopslag en samenwerking, automatisering van workflows, rapportages en dashboards, digitale ondertekening, formulieren, en nog veel meer. In totaal bevat Zoho One meer dan 50 bedrijfsapplicaties die allemaal naadloos samenwerken.',
    },
    {
      question: 'Welke apps zitten er in Zoho One?',
      answer: 'De belangrijkste apps in Zoho One zijn: Zoho CRM voor klantbeheer, Zoho Books voor boekhouding, Zoho Projects voor projectmanagement, Zoho Desk voor klantenservice, Zoho Campaigns voor e-mailmarketing, Zoho People voor HR, Zoho WorkDrive voor documentopslag, Zoho Flow voor automatisering, Zoho Analytics voor rapportages, Zoho Sign voor digitale handtekeningen, Zoho Forms voor formulieren, en nog 40+ andere gespecialiseerde apps voor specifieke bedrijfsprocessen.',
    },
    {
      question: 'Is Zoho One geschikt voor mkb-bedrijven?',
      answer: 'Ja, Zoho One is zeer geschikt voor Nederlandse mkb-bedrijven. Het platform schaalt mee van 5 tot 500+ gebruikers en u betaalt slechts voor de gebruikers die u actief heeft. Door de All Employee pricing (€37 per gebruiker per maand) is het vaak voordeliger dan losse softwarepakketten kopen. MKB-bedrijven kunnen klein starten met de essentiële apps en later uitbreiden zonder extra kosten.',
    },
    {
      question: 'Kan ik losse software vervangen door Zoho One?',
      answer: 'Ja, veel bedrijven vervangen hun losse softwaretools door Zoho One. In plaats van aparte systemen voor CRM, boekhouding, projectmanagement, helpdesk en e-mailmarketing, krijgt u alles in één geïntegreerd platform. Dit elimineert dubbele invoer, voorkomt dataversnippering en bespaart kosten. Wij helpen graag met de analyse welke huidige systemen vervangen kunnen worden door Zoho One.',
    },
    {
      question: 'Kan Guijs Consultancy helpen met de inrichting van Zoho One?',
      answer: 'Ja, als gecertificeerd Zoho Partner helpt Guijs Consultancy met de volledige implementatie van Zoho One. Wij analyseren eerst uw bedrijfsprocessen en adviseren welke apps u echt nodig heeft. Vervolgens richten wij de apps in, bouwen automatiseringen, migreren data en trainen uw team. Zo voorkomt u dat u alleen de basis functies gebruikt en haalt u maximaal rendement uit Zoho One.',
    },
    {
      question: 'Kunnen Zoho One apps met elkaar gekoppeld worden?',
      answer: 'Ja, alle Zoho One apps zijn standaard met elkaar verbonden en delen automatisch data. Zo stroomt een lead uit Zoho CRM automatisch naar Zoho Books voor facturatie, en projecturen uit Zoho Projects kunnen direct gefactureerd worden. Met Zoho Flow kunt u ook specifieke automatiseringen bouwen tussen apps. Wij helpen graag met het opzetten van deze koppelingen volgens uw werkprocessen.',
    },
    {
      question: 'Hoe bepaal ik welke Zoho One apps mijn bedrijf nodig heeft?',
      answer: 'Dit begint met een analyse van uw huidige bedrijfsprocessen. Guijs Consultancy kijkt samen met u naar waar u nu tijd verliest, waar data versnipperd is en welke processen geautomatiseerd kunnen worden. Op basis daarvan adviseren wij welke Zoho One apps het beste passen. Veel bedrijven starten met 5-7 kern apps en breiden later uit. Wij zorgen ervoor dat u niet overweldigd wordt door alle mogelijkheden.',
    },
    {
      question: 'Kan Zoho One gekoppeld worden aan bestaande software?',
      answer: 'Ja, Zoho One kan gekoppeld worden aan vrijwel alle externe systemen via API-koppelingen of via Zoho Flow. Standaard integraties bestaan voor tools zoals Google Workspace, Microsoft 365, Slack, Mailchimp en 1000+ andere platforms. Voor maatwerk koppelingen met specifieke Nederlandse systemen kunnen wij custom API-integraties bouwen. Zo blijft Zoho One samenwerken met uw bestaande infrastructuur.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              Complete Overzicht van Mogelijkheden
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Zoho One Functionaliteiten: Compleet Overzicht van Apps en Mogelijkheden
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Ontdek wat Zoho One allemaal kan. Van CRM en facturatie tot projectmanagement en HR - 
              één platform met 50+ apps voor al uw bedrijfsprocessen.
            </p>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
              <p className="text-lg text-gray-700">
                <strong>Wil je weten welke Zoho One functionaliteiten jouw bedrijf echt nodig heeft?</strong><br />
                Guijs Consultancy helpt met advies, inrichting en automatisering.
              </p>
              <Link to="/contact" className="inline-block mt-4">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Plan een Vrijblijvend Adviesgesprek
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat Maken Zoho One Functionaliteiten Zo Krachtig?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho One bestaat uit een brede verzameling bedrijfsapplicaties waarmee organisaties hun complete 
                bedrijfsvoering kunnen beheren vanuit één geïntegreerd platform. De echte kracht zit niet alleen 
                in de losse apps, maar vooral in hoe deze samenwerken.
              </p>
              <p>
                Waar traditionele bedrijven werken met losse systemen voor CRM, boekhouding, projectmanagement, 
                helpdesk en HR, verbindt Zoho One al deze functies. Data uit uw CRM stroomt automatisch naar 
                facturatie, projecturen worden direct gekoppeld aan klanten, supporttickets zijn zichtbaar bij 
                klantprofielen en marketing campagnes synchroniseren met verkoop.
              </p>
              <p>
                Dit betekent: <strong>geen dubbele invoer, één waarheid over uw bedrijfsdata, volledige 
                automatisering van processen</strong> en complete transparantie voor het management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Functionalities Overview Table */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Overzicht Zoho One Functionaliteiten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              De belangrijkste categorieën functionaliteiten en wat ze voor uw bedrijf betekenen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {functionalities.map((item, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.category}</h3>
                  <p className="text-sm text-gray-500 mb-2 font-semibold">{item.apps}</p>
                  <p className="text-gray-600 mb-3 text-sm"><strong>Gebruik:</strong> {item.purpose}</p>
                  <p className="text-blue-700 text-sm font-semibold">{item.benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sales en CRM Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Sales en CRM Functionaliteiten
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              De basis van elk groeiend bedrijf is goed klantbeheer en een gestructureerd verkoopproces. 
              Zoho One biedt complete CRM functionaliteiten voor het hele salestraject.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Leadbeheer en kwalificatie',
                'Complete klantdatabase met 360-graden inzicht',
                'Sales pipelines en opportunity tracking',
                'Geautomatiseerde offertes en documenten',
                'Intelligente opvolging en reminders',
                'Geautomatiseerde workflows voor salesprocessen',
                'Realtime dashboards en sales rapportages',
                'Klantcommunicatie logging en historie',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-600">
              Met <Link to="/zoho-crm" className="text-blue-700 hover:text-blue-800 font-semibold">Zoho CRM laten inrichten</Link> door 
              Guijs Consultancy krijgt u een salesproces dat perfect aansluit op uw manier van werken.
            </p>
          </div>
        </div>
      </section>

      {/* Finance Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Finance en Administratie Functionaliteiten
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Zoho One automatiseert uw volledige financiële administratie - van offerte tot betaling. 
              Factureer direct vanuit uw CRM en houd realtime inzicht in uw cashflow.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Professionele facturatie en offertes',
                'Betalingsbeheer en herinneringen',
                'Online betalingen via meerdere providers',
                'Complete Nederlandse boekhouding',
                'Abonnementen en terugkerende facturen',
                'Onkostenbeheer en declaraties',
                'Financiële rapportages en prognoses',
                'BTW-aangiftes en belastingrapportages',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-600">
              Lees meer over <Link to="/zoho-books" className="text-blue-700 hover:text-blue-800 font-semibold">Zoho Books implementatie</Link> en 
              hoe wij zorgen voor een naadloze koppeling met uw CRM.
            </p>
          </div>
        </div>
      </section>

      {/* Due to length, I'll continue with remaining sections... */}
      {/* Projects Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Projectmanagement en Urenregistratie
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Voor projectgebaseerde organisaties biedt Zoho One complete projectmanagement functionaliteiten 
              met automatische facturatie van gewerkte uren.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Projectplanning met Gantt charts',
                'Taken en subtaken met verantwoordelijken',
                'Mijlpalen en deadlines tracking',
                'Urenregistratie per project en klant',
                'Team samenwerking en communicatie',
                'Projectrapportages en winstgevendheid',
                'Agile/Scrum ondersteuning',
                'Automatische facturatie van projecturen',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-600">
              Ontdek hoe u <Link to="/zoho-projects" className="text-blue-700 hover:text-blue-800 font-semibold">Zoho Projects kunt gebruiken voor projectmanagement</Link> dat 
              naadloos samenwerkt met uw CRM en facturatie.
            </p>
          </div>
        </div>
      </section>

      {/* Customer Service Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Klantenservice en Support Functionaliteiten
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Lever uitmuntende klantenservice met gestructureerd ticketbeheer, kennisbank en 
              SLA-management - allemaal verbonden met uw klantprofielen.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Multi-channel ticketbeheer (email, chat, telefoon)',
                'Automatische ticket routing en toewijzing',
                'Klantvragen opvolgen met historie',
                'SLA-management met escalaties',
                'Kennisbank voor self-service',
                'Klanttevredenheid surveys',
                'Support rapportages en KPI-tracking',
                'Integratie met CRM voor volledig klantbeeld',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-600">
              Verbeter uw klantenservice met <Link to="/zoho-desk" className="text-blue-700 hover:text-blue-800 font-semibold">Zoho Desk</Link> - 
              volledig geïntegreerd met uw andere Zoho One apps.
            </p>
          </div>
        </div>
      </section>

      {/* Marketing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Marketing en Leadgeneratie Functionaliteiten
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Zoho One biedt complete marketing automation - van e-mailcampagnes tot leadopvolging, 
              allemaal automatisch gesynchroniseerd met uw CRM.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'E-mailmarketing en nieuwsbrieven',
                'Leadformulieren en landingspagina\'s',
                'Marketing automation workflows',
                'Lead scoring en kwalificatie',
                'Segmentatie en personalisatie',
                'Social media marketing en planning',
                'Campagne rapportages en ROI tracking',
                'Automatische lead-to-CRM synchronisatie',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Documents & Collaboration Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Documenten en Samenwerking
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Centrale documentopslag, veilig delen en digitale ondertekening - alles in één platform 
              met automatische koppeling aan klant- en projectdossiers.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Veilige cloud documentopslag',
                'Samenwerken aan bestanden in realtime',
                'Centrale klant- en projectdossiers',
                'Documentendelen met klanten en partners',
                'Digitale ondertekening van contracten',
                'Versiebeheer en documenthistorie',
                'Team samenwerking en communicatie',
                'Integratie met Office en Google Workspace',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HR Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              HR en Medewerkersbeheer
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Digitaliseer uw volledige HR-proces - van werving tot uitdiensttreding, met self-service 
              portaal voor medewerkers en geautomatiseerde workflows.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Complete medewerkersadministratie',
                'Verlofbeheer en aanvragen',
                'HR-processen en workflows',
                'Onboarding nieuwe medewerkers',
                'Performance management en beoordelingen',
                'Timesheet en presentieregistratie',
                'Self-service portaal voor medewerkers',
                'HR-rapportages en analytics',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-600">
              Moderniseer uw HR met <Link to="/zoho-people" className="text-blue-700 hover:text-blue-800 font-semibold">Zoho People</Link> - 
              volledig geïntegreerd met de rest van uw bedrijfssystemen.
            </p>
          </div>
        </div>
      </section>

      {/* Automation Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Automatisering en Koppelingen
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              De echte kracht van Zoho One zit in automatisering. Met Zoho Flow bouwt u workflows die 
              handmatig werk elimineren en apps met elkaar verbinden.
            </p>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Voorbeelden van Automatisering:</h3>
                <ul className="space-y-3">
                  {[
                    'Nieuwe lead in CRM → Automatisch welkomst email via Campaigns',
                    'Gewonnen deal in CRM → Automatisch factuur in Zoho Books',
                    'Betaalde factuur → Automatisch project starten in Zoho Projects',
                    'Support ticket opgelost → Automatisch tevredenheidsenquête versturen',
                    'Nieuw contract getekend → Automatisch onboarding workflow starten',
                    'Projecturen geregistreerd → Automatisch factuurvoorstel genereren',
                  ].map((item, index) => (
                    <li key={`item-${index}`} className="flex items-start space-x-3">
                      <Zap className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-600">
                Met onze automatiserings-expertise bespaart u gemiddeld <strong>10-15 uur per week</strong> op 
                handmatige taken en voorkomt u invoerfouten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reporting Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Rapportages en Dashboards
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Zoho Analytics combineert data uit alle Zoho One apps en geeft u realtime inzicht in uw 
              volledige bedrijfsvoering via professionele dashboards.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                'Custom dashboards per functie (Sales, Finance, Service)',
                'KPI-tracking en doelstellingen',
                'Realtime rapportages uit alle apps',
                'Data uit meerdere bronnen combineren',
                'Automatische rapportage-distributie',
                'Voorspellende analytics en trends',
                'Export naar Excel en PDF',
                'Mobiele dashboards voor management',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Professional Setup Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Waarom de Juiste Inrichting Belangrijk Is
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              Zoho One heeft enorm veel functionaliteiten - maar zonder goede inrichting verliest u 
              snel het overzicht.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <Card className="border-2 border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Zonder Professionele Inrichting:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Te veel apps tegelijk implementeren</li>
                    <li>• Geen duidelijke werkprocessen</li>
                    <li>• Data versnipperd over apps</li>
                    <li>• Handmatig werk blijft bestaan</li>
                    <li>• Team raakt gefrustreerd</li>
                    <li>• Investering levert niet op</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Met Guijs Consultancy:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Start met essentiële apps</li>
                    <li>• Processen eerst, software daarna</li>
                    <li>• Alle data centraal en verbonden</li>
                    <li>• Slimme automatiseringen</li>
                    <li>• Team werkt met plezier</li>
                    <li>• ROI binnen 6-12 maanden</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Onze Aanpak</h3>
              <p className="text-gray-700 mb-4">
                Bij Guijs Consultancy analyseren wij eerst <strong>uw bedrijfsprocessen</strong> en kijken waar 
                u nu tijd verliest. Dan adviseren wij welke Zoho One functionaliteiten u echt nodig heeft en 
                richten we die in volgens uw werkwijze.
              </p>
              <p className="text-gray-700">
                Wij configureren velden, workflows, rechten, koppelingen en rapportages zo dat het systeem 
                aansluit op hoe u werkt - niet andersom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Voor Wie Zijn de Zoho One Functionaliteiten Geschikt?
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              Zoho One is ideaal voor Nederlandse bedrijven die professioneler willen werken en losse 
              systemen willen vervangen.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'MKB-bedrijven met 5-500 medewerkers',
                'Zakelijke dienstverleners en consultants',
                'Vastgoedbedrijven en property managers',
                'Projectorganisaties en bouwbedrijven',
                'Incassobedrijven en debiteurenbeheerders',
                'IT-bedrijven en softwareleveranciers',
                'Bedrijven die meerdere losse tools gebruiken',
                'Organisaties die CRM, finance en projecten willen combineren',
                'Bedrijven die handmatige processen willen automatiseren',
                'Organisaties die beter inzicht willen in hun data',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-gray-700 mb-6">
                Herkent u uw organisatie? Dan kunnen de Zoho One functionaliteiten een perfecte match zijn.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Bespreek uw Mogelijkheden
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Veelgestelde Vragen over Zoho One Functionaliteiten
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              De meest gestelde vragen over wat Zoho One allemaal kan en hoe wij helpen.
            </p>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={`item-${index}`} value={`item-${index}`} className="bg-white border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-700 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-800 rounded-2xl p-12 text-center text-white shadow-2xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Laat Zoho One Logisch Inrichten op Basis van Jouw Bedrijfsprocessen
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Voorkom losse tools, dubbele invoer en onduidelijke workflows. Wij zorgen voor een 
                Zoho One omgeving die perfect aansluit op hoe u werkt.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 group"
                >
                  Neem Contact Op
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              Meer Informatie over Zoho One
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/zoho-one" className="text-blue-700 hover:text-blue-800 font-semibold hover:underline">
                Zoho One Implementatie
              </Link>
              <span className="text-gray-400">|</span>
              <Link to="/zoho-crm" className="text-blue-700 hover:text-blue-800 font-semibold hover:underline">
                Zoho CRM
              </Link>
              <span className="text-gray-400">|</span>
              <Link to="/zoho-books" className="text-blue-700 hover:text-blue-800 font-semibold hover:underline">
                Zoho Books
              </Link>
              <span className="text-gray-400">|</span>
              <Link to="/contact" className="text-blue-700 hover:text-blue-800 font-semibold hover:underline">
                Contact opnemen
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZohoOneFunctionaliteiten;
